import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { emailScheduleService } from '../../../services/emailScheduleService';

const ScheduleForm = ({ schedule, onClose }) => {
  const [formData, setFormData] = useState({
    reportType: 'financial_report',
    reportName: '',
    frequency: 'daily',
    scheduleTime: '09:00',
    dayOfWeek: 1,
    dayOfMonth: 1,
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (schedule) {
      setFormData({
        reportType: schedule?.reportType || 'financial_report',
        reportName: schedule?.reportName || '',
        frequency: schedule?.frequency || 'daily',
        scheduleTime: schedule?.scheduleTime || '09:00',
        dayOfWeek: schedule?.dayOfWeek || 1,
        dayOfMonth: schedule?.dayOfMonth || 1,
        status: schedule?.status || 'active'
      });
    }
  }, [schedule]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    
    try {
      setLoading(true);
      
      let result;
      if (schedule) {
        result = await emailScheduleService?.updateSchedule(schedule?.id, formData);
      } else {
        result = await emailScheduleService?.createSchedule(formData);
      }

      if (!result) {
        setError('No se pudo guardar el programa. Verifica tu conexión e intenta de nuevo.');
        return;
      }
      
      onClose(true);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError(error?.message || 'Error al guardar el programa. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            {schedule ? 'Edit Schedule' : 'Create New Schedule'}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="p-2 hover:bg-muted rounded-lg transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Report Type
            </label>
            <select
              value={formData?.reportType}
              onChange={(e) => setFormData({ ...formData, reportType: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              required
            >
              <option value="financial_report">Financial Report</option>
              <option value="client_analytics">Client Analytics</option>
              <option value="threshold_alerts">Threshold Alerts</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Report Name
            </label>
            <input
              type="text"
              value={formData?.reportName}
              onChange={(e) => setFormData({ ...formData, reportName: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              placeholder="e.g., Daily Financial Summary"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Frequency
              </label>
              <select
                value={formData?.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                required
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Time
              </label>
              <input
                type="time"
                value={formData?.scheduleTime}
                onChange={(e) => setFormData({ ...formData, scheduleTime: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                required
              />
            </div>
          </div>

          {formData?.frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Day of Week
              </label>
              <select
                value={formData?.dayOfWeek}
                onChange={(e) => setFormData({ ...formData, dayOfWeek: parseInt(e?.target?.value) })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
                <option value={0}>Sunday</option>
              </select>
            </div>
          )}

          {formData?.frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Day of Month
              </label>
              <input
                type="number"
                min="1"
                max="28"
                value={formData?.dayOfMonth}
                onChange={(e) => setFormData({ ...formData, dayOfMonth: parseInt(e?.target?.value) })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={formData?.status}
              onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              required
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={20} className="inline mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                schedule ? 'Update Schedule' : 'Create Schedule'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleForm;